import { useState, useEffect, useRef } from 'react';
import { PlusCircle, Pencil, X, Check, Server, RefreshCw, Radio, Wifi, WifiOff, Trash2 } from 'lucide-react';
import { apiFetch } from '../api/client';

interface ClientDatabase {
  id: number;
  name: string;
  db_id: number;
  company_ids: number[];
  area_ids: number[] | 'all';
  active: boolean;
  source: 'native' | 'ei_analytic';
  notes: string | null;
  updated_at: string;
}

type GatewayStatus = 'fresh' | 'stale' | 'offline' | 'unknown' | 'disabled';

interface Gateway {
  id: number;
  gateway_serial: string;
  label: string | null;
  db_id: number;
  db_name: string | null;
  notes: string | null;
  // Status is DERIVED server-side from landed data, per pathway. It is not the
  // old `online` boolean, which was a 15-minute rule against a heartbeat column
  // that only the MQTT path ever wrote — so an HTTP-only gateway read as
  // permanently offline, and a serial-format mismatch made a healthy gateway
  // read "Never seen" for two months.
  status: GatewayStatus;
  age_minutes: number | null;
  pathways: string[];
  last_mqtt_at: string | null;
  last_http_at: string | null;
  last_seen_at: string | null;   // broker heartbeat — diagnostic only
  sensor_count: number;
  expected_cadence_minutes: number;
  offline_after_minutes: number;
  enabled: boolean;
  auto_registered: boolean;
  token_prefix: string | null;
  has_token: boolean;
  created_at: string;
  first_seen_at: string | null;
}

interface IssuedToken {
  db_id: number;
  token: string;
  token_prefix: string;
  warning: string;
}

// A gateway that fails auth never reaches the registry — by design, so an
// unauthenticated POST can't invent gateways. The cost is that "no gateway",
// "gateway misconfigured", and "someone probing" all look identical from the
// table above. This panel is the difference.
interface RejectedAttempt {
  gateway_serial: string;
  ip: string;
  count: number;
  first_at: string;
  last_at: string;
  reason: string;
}

interface RejectedResponse {
  rejected: RejectedAttempt[];
  total_attempts: number;
  distinct_gateways: number;
  note: string;
}

// Token METADATA, keyed by database. Deliberately NOT derived from the gateway
// rows: a token is per-DATABASE, but the only place to mint one used to be a
// button inside a gateway row — so a brand-new database had no way to get a
// token until a gateway existed, and a gateway cannot register itself until it
// authenticates with... the token. That deadlock is why this is fetched
// separately and rendered on the database.
interface TokenMeta {
  id: number;
  db_id: number;
  token_prefix: string | null;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  db_name: string | null;
}

const GW_STATUS: Record<GatewayStatus, { label: string; cls: string; dot: string }> = {
  fresh:    { label: 'Fresh',    cls: 'text-green-600',  dot: 'bg-green-500' },
  stale:    { label: 'Stale',    cls: 'text-amber-600',  dot: 'bg-amber-500' },
  offline:  { label: 'Offline',  cls: 'text-red-600',    dot: 'bg-red-500' },
  // Registered, but nothing landed is attributable to it yet — NOT an outage,
  // and deliberately never alerts. Distinct from Offline on purpose.
  unknown:  { label: 'No data yet', cls: 'text-gray-400', dot: 'bg-gray-300' },
  disabled: { label: 'Disabled', cls: 'text-gray-400',   dot: 'bg-gray-300' },
};

const fmtAge = (m: number | null): string => {
  if (m === null) return '—';
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)}m ago`;
  if (m < 60 * 48) return `${(m / 60).toFixed(1)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

interface SyncStatus {
  running: boolean;
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  output: string[];
}

const EMPTY_FORM = {
  name: '',
  db_id: '',
  company_ids: '1',
  area_ids: 'all' as 'all' | string,
  area_mode: 'all' as 'all' | 'specific',
  source: 'native' as 'native' | 'ei_analytic',
  notes: '',
};

// `source` is no longer a tag — it decides three things, so the form asks for it
// FIRST and the rest of the fields follow from it:
//   1. whether a db_id is required (an EI client's must equal its EI database id;
//      a PCR-native client has no EI account, so the server mints one)
//   2. which fields mean anything at all (company_ids / area_ids are EI sync
//      parameters — they are read by the poller, which never sees a native client)
//   3. whether the nightly reconciler expects this site in the poller's CONFIG
//      (reconcile.py check A2). Before this, a native client would have been
//      reported ABSENT-FROM-CONFIG every night forever, and the only way to
//      silence that was a liveness_status that ALSO suppressed its real
//      data-outage alarm.
const SOURCE_COPY = {
  native: {
    label: 'PCR-native (gateway ingest)',
    hint: 'No EI-Analytic account. The server assigns an internal database id.',
  },
  ei_analytic: {
    label: 'EI-Analytic (API polling)',
    hint: 'Mirrors an existing EI-Analytic database. Needs that database’s id.',
  },
} as const;

export function DatabaseManagement() {
  const [dbs, setDbs]           = useState<ClientDatabase[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [sync, setSync]         = useState<SyncStatus | null>(null);
  const [syncing, setSyncing]   = useState(false);
  const [showLog, setShowLog]   = useState(false);
  const syncPollRef             = useRef<ReturnType<typeof setInterval> | null>(null);
  const logEndRef               = useRef<HTMLDivElement>(null);

  // Gateways
  const [gateways, setGateways]         = useState<Gateway[]>([]);
  const [gwForm, setGwForm]             = useState({ serial: '', db_id: '', cadence: '60', notes: '' });
  const [gwFormOpen, setGwFormOpen]     = useState(false);
  const [gwSaving, setGwSaving]         = useState(false);
  const [gwError, setGwError]           = useState('');
  const [gwFilter, setGwFilter]         = useState<number | 'all'>('all');
  const [issued, setIssued]             = useState<IssuedToken | null>(null);
  const [rejected, setRejected]         = useState<RejectedResponse | null>(null);
  const [tokens, setTokens]             = useState<TokenMeta[]>([]);
  const [tokenBusy, setTokenBusy]       = useState<number | null>(null);
  const [copied, setCopied]             = useState(false);

  const loadGateways = () =>
    apiFetch<Gateway[]>('/admin/gateways')
      .then(d => { setGateways(d ?? []); setGwError(''); })
      // Was silently swallowed (.catch(() => {})), so a 404/500 rendered as
      // "No gateways registered." — indistinguishable from an empty fleet.
      .catch(() => setGwError('Failed to load gateways.'));

  const loadRejected = () =>
    apiFetch<RejectedResponse>('/gateway/rejected')
      .then(d => setRejected(d ?? null))
      .catch(() => {});   // diagnostic only — never block the page on it

  const loadTokens = () =>
    apiFetch<TokenMeta[]>('/admin/gateway-tokens')
      .then(d => setTokens(d ?? []))
      .catch(() => setTokens([]));

  const tokenFor = (dbId: number) => tokens.find(t => t.db_id === dbId) ?? null;

  const issueToken = async (dbId: number, rotating: boolean) => {
    if (rotating && !confirm(
      `Rotate the ingest token for this database?\n\n` +
      `The current token stops working IMMEDIATELY. Every gateway on this ` +
      `database will be rejected (401) until you configure the new token on it.`
    )) return;
    setTokenBusy(dbId);
    setGwError('');
    try {
      const t = await apiFetch<IssuedToken>(`/admin/gateway-tokens/${dbId}`, { method: 'POST' });
      setIssued(t);          // shown ONCE — the server stores only a hash
      setCopied(false);
      loadGateways();
      loadTokens();
    } catch {
      setGwError('Failed to issue token.');
    } finally {
      setTokenBusy(null);
    }
  };

  const revokeToken = async (dbId: number) => {
    if (!confirm(
      `Revoke the ingest token for this database?\n\n` +
      `Every gateway on it will be rejected (401) until a new token is issued ` +
      `and configured on the device. Data will stop landing.`
    )) return;
    setTokenBusy(dbId);
    try {
      await apiFetch(`/admin/gateway-tokens/${dbId}`, { method: 'DELETE' });
      loadGateways();
      loadTokens();
    } catch {
      setGwError('Failed to revoke token.');
    } finally {
      setTokenBusy(null);
    }
  };

  const shownGateways = gwFilter === 'all'
    ? gateways
    : gateways.filter(g => g.db_id === gwFilter);

  const gwDbIds = Array.from(new Set(gateways.map(g => g.db_id)));

  const load = () => {
    setLoading(true);
    apiFetch<ClientDatabase[]>('/admin/databases')
      .then(data => setDbs(data ?? []))
      .catch(() => setError('Failed to load databases'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); loadGateways(); loadRejected(); loadTokens(); }, []);

  // Fetch sync status once on mount so we can show last-run state
  useEffect(() => {
    apiFetch<SyncStatus>('/admin/sync-tree/status').then(s => { if (s) setSync(s); }).catch(() => {});
  }, []);

  // Auto-scroll log to bottom when new output arrives
  useEffect(() => {
    if (showLog) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sync?.output?.length, showLog]);

  // Poll while running; clear when done
  useEffect(() => {
    if (sync?.running) {
      syncPollRef.current = setInterval(() => {
        apiFetch<SyncStatus>('/admin/sync-tree/status').then(s => {
          if (s) {
            setSync(s);
            if (!s.running && syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
              load(); // refresh DB list after sync
            }
          }
        }).catch(() => {});
      }, 2000);
    }
    return () => {
      if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
    };
  }, [sync?.running]);

  const triggerSync = async () => {
    setSyncing(true);
    setShowLog(true);
    try {
      await apiFetch('/admin/sync-tree', { method: 'POST' });
      const s = await apiFetch<SyncStatus>('/admin/sync-tree/status');
      if (s) setSync(s);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to start sync-tree');
    } finally {
      setSyncing(false);
    }
  };

  const saveGateway = async () => {
    if (!gwForm.serial.trim()) { setGwError('Enter a gateway serial.'); return; }
    if (!gwForm.db_id) { setGwError('Select a database.'); return; }
    setGwSaving(true); setGwError('');
    try {
      await apiFetch('/admin/gateways', {
        method: 'POST',
        body: JSON.stringify({
          gateway_serial: gwForm.serial.trim(),
          db_id: parseInt(gwForm.db_id, 10),
          expected_cadence_minutes: parseInt(gwForm.cadence, 10) || 60,
          notes: gwForm.notes.trim() || null,
        }),
      });
      setGwForm({ serial: '', db_id: '', cadence: '60', notes: '' });
      setGwFormOpen(false);
      loadGateways();
    } catch (err: any) {
      setGwError(err?.message ?? 'Failed to register gateway.');
    } finally {
      setGwSaving(false);
    }
  };

  const deleteGateway = async (gw: Gateway) => {
    // Removing an OBSERVED gateway is near-pointless — the next message it
    // delivers re-registers it. Say so rather than let the row silently return.
    const willReturn = gw.auto_registered && gw.status !== 'offline';
    if (!confirm(
      `Remove gateway ${gw.gateway_serial} from the registry?` +
      (willReturn
        ? `\n\nNote: this gateway is currently delivering data, so it will ` +
          `re-register itself automatically on its next message. To stop ` +
          `alerting on it, disable it instead.`
        : '')
    )) return;
    try {
      await apiFetch(`/admin/gateways/${gw.id}`, { method: 'DELETE' });
      loadGateways();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove gateway.');
    }
  };

  const toggleEnabled = async (gw: Gateway) => {
    try {
      await apiFetch(`/admin/gateways/${gw.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !gw.enabled }),
      });
      loadGateways();
    } catch {
      setGwError('Failed to update gateway.');
    }
  };

  const resetForm = () => { setForm(EMPTY_FORM); setShowForm(false); setEditId(null); };

  const openEdit = (db: ClientDatabase) => {
    setEditId(db.id);
    setShowForm(true);
    const areaIsAll = db.area_ids === 'all' || (Array.isArray(db.area_ids) && db.area_ids.length === 0);
    setForm({
      name:      db.name,
      db_id:     String(db.db_id),
      company_ids: Array.isArray(db.company_ids) ? db.company_ids.join(', ') : '1',
      area_ids:  areaIsAll ? 'all' : (Array.isArray(db.area_ids) ? db.area_ids.join(', ') : ''),
      area_mode: areaIsAll ? 'all' : 'specific',
      source:    db.source ?? 'ei_analytic',
      notes:     db.notes ?? '',
    });
    setError(''); setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);

    const isNative = form.source === 'native';

    if (!isNative && !form.db_id.trim()) {
      setError('An EI-Analytic client needs its EI-Analytic database id.');
      setSaving(false);
      return;
    }

    const company_ids = form.company_ids.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    const area_ids    = form.area_mode === 'all' ? 'all'
      : form.area_ids.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));

    // A native client sends NO db_id — the server mints one (backend/db_registry.py).
    // company_ids / area_ids are EI sync parameters and are meaningless here, so
    // they are omitted rather than sent as a plausible-looking default: a stored
    // value nobody reads is the thing that made `source` itself decorative.
    const body = isNative
      ? {
          name:   form.name.trim(),
          source: 'native',
          notes:  form.notes.trim() || null,
        }
      : {
          name:        form.name.trim(),
          db_id:       parseInt(form.db_id, 10),
          company_ids,
          area_ids,
          source:      form.source,
          notes:       form.notes.trim() || null,
        };

    try {
      if (editId !== null) {
        await apiFetch(`/admin/databases/${editId}`, { method: 'PATCH', body: JSON.stringify(body) });
        setSuccess(`"${body.name}" updated.`);
      } else {
        const created = await apiFetch<ClientDatabase>('/admin/databases', {
          method: 'POST', body: JSON.stringify(body),
        });
        // NOT "picked up on the next poll cycle" — that was false for every row
        // ever created here. The poller reads CONFIG['databases'] in
        // vibration_monitor.py and has never read this table. Say what actually
        // happens next, per source, because they are different next steps.
        setSuccess(isNative
          ? `"${body.name}" registered as database ${created?.db_id ?? '(assigned)'}. `
            + `Next: add its machine, points and axes, then register the gateway `
            + `and issue its ingest token — in that order.`
          : `"${body.name}" registered. It is NOT polled yet: add it to `
            + `CONFIG['databases'] in vibration_monitor.py, or the nightly `
            + `reconciler will report it as absent from the poller's config.`);
      }
      resetForm();
      load();
      loadTokens();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save database');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (db: ClientDatabase) => {
    const action = db.active ? 'deactivate' : 're-activate';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${db.name}"?`)) return;
    try {
      if (db.active) {
        await apiFetch(`/admin/databases/${db.id}`, { method: 'DELETE' });
      } else {
        await apiFetch(`/admin/databases/${db.id}`, { method: 'PATCH', body: JSON.stringify({ active: true }) });
      }
      setSuccess(`"${db.name}" ${db.active ? 'deactivated' : 're-activated'}.`);
      load();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update database');
    }
  };

  const syncLabel = () => {
    if (!sync) return null;
    if (sync.running) return <span className="text-blue-600 text-xs">Running…</span>;
    if (sync.finished_at) {
      const ok = sync.exit_code === 0;
      return (
        <span className={`text-xs ${ok ? 'text-green-600' : 'text-red-500'}`}>
          {ok ? 'Completed' : `Failed (exit ${sync.exit_code})`}
          {' · '}
          <button onClick={() => setShowLog(l => !l)} className="underline">
            {showLog ? 'hide log' : 'show log'}
          </button>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-gray-900">Database Registry</h2>
          {/* This table drives auth scoping and UI labels. It has NEVER driven
              polling — the poller reads CONFIG['databases'] in
              vibration_monitor.py — so "changes take effect on the next poll
              cycle" was false here for every row, EI or native. */}
          <p className="text-gray-500 text-sm mt-1">
            Client databases: access scoping, labels, and gateway ingest tokens.
            EI-Analytic clients must also be listed in the poller&rsquo;s config to be polled.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={triggerSync}
              disabled={syncing || sync?.running}
              title="Re-sync machine hierarchy from EI-Analytic"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${sync?.running ? 'animate-spin text-blue-500' : ''}`} />
              Sync Tree
            </button>
            <span className="min-w-[120px]">{syncLabel()}</span>
          </div>
          {!showForm && (
            <button
              onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); setError(''); setSuccess(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <PlusCircle className="w-4 h-4" />
              Add Database
            </button>
          )}
        </div>
      </div>

      {/* Sync log */}
      {showLog && sync && sync.output.length > 0 && (
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400 font-mono">sync-tree output</span>
            <button onClick={() => setShowLog(false)} className="text-gray-500 hover:text-gray-300">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto font-mono text-xs text-green-400 space-y-0.5">
            {sync.output.map((line, i) => <div key={i}>{line || ' '}</div>)}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {error   && <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-gray-900 mb-5">{editId ? 'Edit Database' : 'Register New Database'}</h3>

          {/* The EI branch cannot be completed here, and says so BEFORE the submit
              rather than after. Creating an EI client needs its EI-Analytic database
              id AND a matching CONFIG['databases'] entry in the poller, so it stays a
              privileged out-of-band act — the API returns 501 by design, not by
              accident. Saying that up front is the difference between a documented
              boundary and what looks like a broken button. */}
          {!editId && form.source === 'ei_analytic' && (
            <div className="mb-5 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
              <p className="font-medium mb-1">An EI-Analytic client is registered out of band.</p>
              <p className="text-xs leading-relaxed">
                It needs its EI-Analytic database id and a matching entry in the poller&rsquo;s
                <span className="font-mono"> CONFIG[&apos;databases&apos;]</span>, so it is created by an
                operator with database privileges rather than through this form. The
                <strong> Name</strong> must then match the EI-Analytic login response exactly
                (case-sensitive). Use this form to register <strong>PCR-native</strong> clients.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Source FIRST: it decides whether a db_id is required and whether the
                EI sync fields below mean anything at all. */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">Client type</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                {(['native', 'ei_analytic'] as const).map(s => (
                  <label key={s} className={`flex-1 flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    form.source === s ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" className="mt-0.5" value={s} checked={form.source === s}
                      onChange={() => setForm(f => ({ ...f, source: s }))} />
                    <span>
                      <span className="block text-sm text-gray-800">{SOURCE_COPY[s].label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{SOURCE_COPY[s].hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Database Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={form.source === 'native' ? 'e.g. Acme Foods' : 'e.g. Berry'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {form.source === 'native'
                    ? 'Any name. Client users are scoped by matching this exactly.'
                    : 'Must match EI-Analytic exactly'}
                </p>
              </div>
              {form.source === 'ei_analytic' ? (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">EI-Analytic DB ID <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={form.db_id}
                    onChange={e => setForm(f => ({ ...f, db_id: e.target.value }))}
                    placeholder="e.g. 699"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Found in EI-Analytic login response</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Database ID</label>
                  <div className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-400 bg-gray-50 select-none">
                    Assigned by the server
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    An internal tenant id. It is never sent to EI-Analytic.
                  </p>
                </div>
              )}
            </div>

            {/* EI sync parameters. Hidden for a native client because the poller is
                the only reader and it never polls one — a value stored here for an
                native client would be inert, and inert stored values are what made
                `source` itself look meaningful for months. */}
            {form.source === 'ei_analytic' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Company IDs to sync</label>
                  <input
                    type="text"
                    value={form.company_ids}
                    onChange={e => setForm(f => ({ ...f, company_ids: e.target.value }))}
                    placeholder="1, 2, 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Comma-separated EI-Analytic company IDs</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Areas to sync</label>
                  <div className="flex gap-3 mb-2">
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                      <input type="radio" value="all" checked={form.area_mode === 'all'} onChange={() => setForm(f => ({ ...f, area_mode: 'all' }))} />
                      All areas
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                      <input type="radio" value="specific" checked={form.area_mode === 'specific'} onChange={() => setForm(f => ({ ...f, area_mode: 'specific' }))} />
                      Specific IDs
                    </label>
                  </div>
                  {form.area_mode === 'specific' && (
                    <input
                      type="text"
                      value={form.area_ids === 'all' ? '' : form.area_ids}
                      onChange={e => setForm(f => ({ ...f, area_ids: e.target.value }))}
                      placeholder="1, 2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                    />
                  )}
                  <p className="text-xs text-amber-700 mt-1">
                    List area ids EXPLICITLY for a new database. &ldquo;All&rdquo; resolves
                    through the shared <code>areas</code> table and silently under-returns.
                  </p>
                </div>
              </div>
            )}

            {form.source === 'native' && !editId && (
              <div className="px-4 py-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg text-sm">
                <p className="font-medium mb-1">Order matters after this.</p>
                <p className="text-blue-800 text-xs leading-relaxed">
                  Add the machine, its points and axes <em>first</em>, then register the
                  device, then the gateway + its ingest token. A sensor registered before
                  its axes exist binds to nothing, and its captures are answered
                  <span className="font-mono"> 200 {'{'}&quot;processed&quot;: false{'}'}</span> and
                  discarded — the device list will show only a blank timestamp, never the cause.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — client name, site, contact..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-60"
              >
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Register Database'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : dbs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No databases registered.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Name</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">DB ID</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Source</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Companies</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Areas</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Status</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Ingest token</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Notes</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {dbs.map(db => (
                <tr key={db.id} className={`border-b border-gray-50 transition-colors ${db.active ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-900">{db.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-gray-600 text-xs">{db.db_id}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      db.source === 'ei_analytic'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-purple-50 text-purple-700'
                    }`}>
                      {db.source === 'ei_analytic' ? 'EI-Analytic' : 'Native'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {Array.isArray(db.company_ids) ? db.company_ids.join(', ') : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {db.area_ids === 'all' || !db.area_ids || (Array.isArray(db.area_ids) && db.area_ids.length === 0)
                      ? <span className="text-gray-400 text-xs">All</span>
                      : (Array.isArray(db.area_ids) ? db.area_ids.join(', ') : String(db.area_ids))
                    }
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      db.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {db.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {/* Per DATABASE, not per gateway. A token is scoped to a db_id,
                      and a new database has no gateway yet — minting had to be
                      possible before the first device ever connects. */}
                  <td className="px-5 py-3 whitespace-nowrap">
                    {(() => {
                      const t = tokenFor(db.db_id);
                      if (!db.active) return <span className="text-gray-300 text-xs">—</span>;
                      return t ? (
                        <div className="flex items-center gap-1.5">
                          <code className="text-[11px] text-gray-500 font-mono">{t.token_prefix}…</code>
                          <button
                            onClick={() => issueToken(db.db_id, true)}
                            disabled={tokenBusy === db.db_id}
                            className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                            title="Rotate — the current token stops working immediately"
                          >Rotate</button>
                          <button
                            onClick={() => revokeToken(db.db_id)}
                            disabled={tokenBusy === db.db_id}
                            className="text-[11px] text-red-500 hover:underline disabled:opacity-50"
                            title="Revoke — this database stops accepting gateway posts"
                          >Revoke</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => issueToken(db.db_id, false)}
                          disabled={tokenBusy === db.db_id}
                          className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                        >
                          {tokenBusy === db.db_id ? 'Generating…' : 'Generate'}
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3 text-gray-400 text-xs max-w-xs truncate">{db.notes ?? '—'}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(db)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors group"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                      </button>
                      <button
                        onClick={() => toggleActive(db)}
                        className={`p-1.5 rounded-lg transition-colors group ${db.active ? 'hover:bg-red-50' : 'hover:bg-green-50'}`}
                        title={db.active ? 'Deactivate' : 'Re-activate'}
                      >
                        {db.active
                          ? <X className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
                          : <Check className="w-4 h-4 text-gray-400 group-hover:text-green-600" />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {/* Gateway Registry */}
      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-gray-800 font-medium">Gateways</h3>
            {/* Copy fix: this section serves BOTH pathways, not just MQTT. And
                status is per-gateway cadence against landed data — never a
                global 15-minute rule against a heartbeat column. */}
            <p className="text-gray-400 text-xs mt-0.5">
              Assign gateways to databases. Status is derived from data actually landed
              by each gateway&rsquo;s sensors, per pathway (MQTT and HTTP), against that
              gateway&rsquo;s own cadence.
            </p>
          </div>
          <button
            onClick={() => { setGwFormOpen(true); setGwError(''); }}
            className="self-start sm:self-auto flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Add Gateway
          </button>
        </div>

        {gwDbIds.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-400">Database:</span>
            <button
              onClick={() => setGwFilter('all')}
              className={`px-2.5 py-1 rounded-full border transition-colors ${
                gwFilter === 'all'
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
            >
              All ({gateways.length})
            </button>
            {gwDbIds.map(id => {
              const n = gateways.filter(g => g.db_id === id).length;
              const nm = gateways.find(g => g.db_id === id)?.db_name ?? `db ${id}`;
              return (
                <button
                  key={id}
                  onClick={() => setGwFilter(id)}
                  className={`px-2.5 py-1 rounded-full border transition-colors ${
                    gwFilter === id
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  {nm} ({n})
                </button>
              );
            })}
          </div>
        )}

        {/* Show-once token. The server keeps only sha256(token); this dialog is
            the single moment the plaintext exists anywhere outside the device. */}
        {issued && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <h4 className="text-amber-900 text-sm font-medium">
                  Ingest token for {gateways.find(g => g.db_id === issued.db_id)?.db_name ?? `db ${issued.db_id}`}
                </h4>
                <p className="text-amber-800 text-xs mt-1">{issued.warning}</p>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 min-w-0 px-3 py-2 bg-white border border-amber-300 rounded-lg font-mono text-xs text-gray-800 break-all">
                    {issued.token}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(issued.token);
                      setCopied(true);
                    }}
                    className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-xs whitespace-nowrap"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-amber-700 text-xs mt-3">
                  Paste this into the <span className="font-mono">EI-Auth2</span> field on each
                  gateway for this database. Until you do, that gateway&rsquo;s posts are
                  rejected (401) and its data will not land.
                </p>
              </div>
              <button onClick={() => setIssued(null)} className="p-1 hover:bg-amber-100 rounded-lg shrink-0" title="Dismiss">
                <X className="w-4 h-4 text-amber-700" />
              </button>
            </div>
          </div>
        )}

        {gwError && (
          <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">{gwError}</div>
        )}

        {gwFormOpen && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h4 className="text-gray-800 text-sm font-medium mb-4">Register Gateway</h4>
            <p className="text-gray-400 text-xs mb-4">
              Gateways that deliver data register themselves automatically — add one by hand
              only to pre-register a device before it ships.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Gateway Serial <span className="text-red-400">*</span></label>
                <div className="relative">
                  <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={gwForm.serial}
                    onChange={e => setGwForm(f => ({ ...f, serial: e.target.value }))}
                    /* Digits — what the gateway actually transmits. The
                       'PhantomGW-' prefix is a sticker on the box, and storing
                       it is what made the heartbeat silently match zero rows.
                       The API strips it if pasted, but don't teach it here. */
                    placeholder="e.g. 589245234"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Database <span className="text-red-400">*</span></label>
                <select
                  value={gwForm.db_id}
                  onChange={e => setGwForm(f => ({ ...f, db_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select database…</option>
                  {dbs.filter(d => d.active).map(d => (
                    <option key={d.db_id} value={d.db_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cadence (min)</label>
                <input
                  type="number"
                  min={1}
                  value={gwForm.cadence}
                  onChange={e => setGwForm(f => ({ ...f, cadence: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
                <p className="text-gray-400 text-[11px] mt-1">How often it should report.</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  value={gwForm.notes}
                  onChange={e => setGwForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Site, location…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            {gwError && <p className="text-red-600 text-xs mt-3">{gwError}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={saveGateway} disabled={gwSaving}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60">
                {gwSaving ? 'Saving…' : 'Register'}
              </button>
              <button onClick={() => { setGwFormOpen(false); setGwError(''); }}
                className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {shownGateways.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              {gateways.length === 0
                ? 'No gateways registered. Any gateway that delivers data registers itself automatically.'
                : 'No gateways for this database.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Gateway</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Database</th>
                  {/* Per-pathway, not one dot: a gateway can use both, and which
                      one is delivering is the first thing you need when it stops. */}
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Last MQTT</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Last HTTP</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Sensors</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Token</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {shownGateways.map(gw => {
                  const s = GW_STATUS[gw.status] ?? GW_STATUS.unknown;
                  return (
                    <tr key={gw.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`flex items-center gap-1.5 text-xs font-medium ${s.cls}`}>
                          {gw.status === 'fresh'
                            ? <Wifi className="w-3.5 h-3.5" />
                            : <WifiOff className="w-3.5 h-3.5" />}
                          {s.label}
                        </span>
                        <span className="text-gray-400 text-[11px]">
                          {gw.status === 'unknown' ? 'no data attributed yet' : fmtAge(gw.age_minutes)}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="font-mono text-gray-800 text-xs">{gw.gateway_serial}</div>
                        <div className="text-gray-400 text-[11px]">
                          {gw.label ?? (gw.auto_registered ? 'auto-discovered' : 'manual')}
                          {' · '}every {gw.expected_cadence_minutes}m
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{gw.db_name ?? `db_id ${gw.db_id}`}</td>
                      <td className={`px-5 py-3 text-xs whitespace-nowrap ${gw.last_mqtt_at ? 'text-gray-600' : 'text-gray-300'}`}>
                        {gw.last_mqtt_at ?? '—'}
                      </td>
                      <td className={`px-5 py-3 text-xs whitespace-nowrap ${gw.last_http_at ? 'text-gray-600' : 'text-gray-300'}`}>
                        {gw.last_http_at ?? '—'}
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{gw.sensor_count}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {gw.has_token ? (
                          <div className="flex items-center gap-1.5">
                            <code className="text-[11px] text-gray-500 font-mono">{gw.token_prefix}…</code>
                            <button
                              onClick={() => issueToken(gw.db_id, true)}
                              disabled={tokenBusy === gw.db_id}
                              className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                              title="Rotate — the current token stops working immediately"
                            >Rotate</button>
                            <button
                              onClick={() => revokeToken(gw.db_id)}
                              disabled={tokenBusy === gw.db_id}
                              className="text-[11px] text-red-500 hover:underline disabled:opacity-50"
                              title="Revoke — this database stops accepting gateway posts"
                            >Revoke</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => issueToken(gw.db_id, false)}
                            disabled={tokenBusy === gw.db_id}
                            className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                          >
                            {tokenBusy === gw.db_id ? 'Generating…' : 'Generate'}
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleEnabled(gw)}
                            className={`p-1.5 rounded-lg transition-colors group ${gw.enabled ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`}
                            title={gw.enabled ? 'Disable (stops alerting on it)' : 'Re-enable'}
                          >
                            {gw.enabled
                              ? <X className="w-4 h-4 text-gray-300 group-hover:text-amber-500" />
                              : <Check className="w-4 h-4 text-gray-300 group-hover:text-green-600" />}
                          </button>
                          <button onClick={() => deleteGateway(gw)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group" title="Remove from registry">
                            <Trash2 className="w-4 h-4 text-gray-300 group-hover:text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
        <p className="text-gray-400 text-[11px]">
          Timestamps are Eastern, matching how measurements are stored. A token is per
          database — every gateway on a database shares it, and it can only write that
          database&rsquo;s sensors.
        </p>

        {/* Rejected attempts. Sits under the table because it explains the table's
            blind spot: a gateway that fails auth is never registered, so a
            misconfigured device is indistinguishable from an absent one. During
            the 2026-07-15 token cutover, 201 POSTs arrived and were invisible. */}
        {rejected && rejected.rejected.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
              <h4 className="text-amber-900 text-sm font-medium">
                Rejected attempts ({rejected.total_attempts} from {rejected.distinct_gateways} gateway
                {rejected.distinct_gateways === 1 ? '' : 's'})
              </h4>
              <p className="text-amber-700 text-xs mt-0.5">
                Gateways that reached the server and were turned away. They are <em>not</em> registered
                — a serial here is unverified, self-reported by the caller. Since the last backend restart.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-5 py-2 text-gray-600 font-medium">Serial (claimed)</th>
                    <th className="text-left px-5 py-2 text-gray-600 font-medium">IP</th>
                    <th className="text-left px-5 py-2 text-gray-600 font-medium">Attempts</th>
                    <th className="text-left px-5 py-2 text-gray-600 font-medium">Last</th>
                    <th className="text-left px-5 py-2 text-gray-600 font-medium">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {rejected.rejected.map(r => {
                    const known = gateways.some(g => g.gateway_serial === r.gateway_serial);
                    return (
                      <tr key={`${r.gateway_serial}|${r.ip}`} className="border-b border-gray-50">
                        <td className="px-5 py-2 font-mono text-xs text-gray-800">
                          {r.gateway_serial}
                          {/* A registered gateway being rejected is a MISCONFIGURED
                              device. An unregistered one is an unknown or a probe.
                              That distinction is the whole point of this panel. */}
                          <span className={`ml-2 text-[10px] ${known ? 'text-amber-600' : 'text-gray-400'}`}>
                            {known ? 'registered → misconfigured' : 'unknown'}
                          </span>
                        </td>
                        <td className="px-5 py-2 font-mono text-xs text-gray-500">{r.ip}</td>
                        <td className="px-5 py-2 text-gray-700 text-xs">{r.count}</td>
                        <td className="px-5 py-2 text-gray-400 text-xs">
                          {r.last_at.slice(11, 19)}
                        </td>
                        <td className="px-5 py-2 text-gray-500 text-xs">{r.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
